/*
 This file is part of GNU Taler
 (C) 2022-2024, 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.
*/

import {
  HttpStatusCode,
  TalerCorebankApi,
  TalerErrorCode,
  TranslatedString,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  RouteDefinition,
  useAsyncAction,
  useBankCoreApiContext,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { RefObject, VNode, h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { AuthAlert, AuthCard, FieldError, PasswordField } from "./AuthForm.js";
import {
  PASSWORD_MAX_LENGTH,
  USERNAME_MAX_LENGTH,
  isBlankName,
  validatePassword,
  validateUsername,
} from "./auth-validation.js";
import {
  translatePasswordError,
  translateUsernameError,
  useLoginOperation,
} from "./LoginForm.js";

const TALER_SCREEN_ID = 110;

interface RegistrationValues {
  name: string;
  username: string;
  password: string;
}

type RegistrationField = "name" | "username" | "password" | "confirmation";

export interface RegistrationFormViewProps {
  routeLogin: RouteDefinition;
  running?: boolean;
  operationError?: TranslatedString;
  onSubmit(values: RegistrationValues): void | Promise<void>;
}

RegistrationFormView.SCREEN_ID = TALER_SCREEN_ID;
export function RegistrationFormView({
  routeLogin,
  running = false,
  operationError,
  onSubmit,
}: RegistrationFormViewProps): VNode {
  const { i18n } = useTranslationContext();
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState<
    Partial<Record<RegistrationField, boolean>>
  >({});
  const nameRef = useRef<HTMLInputElement | null>(null);
  const usernameRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const confirmationRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    nameRef.current?.focus({ preventScroll: true });
  }, []);

  const nameError = isBlankName(name)
    ? i18n.str`Enter your full name.`
    : undefined;
  const usernameError = translateUsernameError(
    validateUsername(username),
    i18n,
  );
  const passwordError = translatePasswordError(
    validatePassword(password, true),
    i18n,
  );
  const confirmationError = !confirmation
    ? i18n.str`Repeat your password.`
    : confirmation !== password
      ? i18n.str`The passwords do not match.`
      : undefined;

  const markTouched = (field: RegistrationField): void => {
    setTouched((current) => ({ ...current, [field]: true }));
  };

  const submit = async (event: Event): Promise<void> => {
    event.preventDefault();
    if (running) return;
    setSubmitted(true);
    const invalidFields: Array<
      [TranslatedString | undefined, RefObject<HTMLInputElement>]
    > = [
      [nameError, nameRef],
      [usernameError, usernameRef],
      [passwordError, passwordRef],
      [confirmationError, confirmationRef],
    ];
    const firstInvalid = invalidFields.find(([error]) => !!error);
    if (firstInvalid) {
      firstInvalid[1].current?.focus();
      return;
    }
    await onSubmit({ name: name.trim(), username, password });
  };

  return (
    <AuthCard
      title={i18n.str`Create your bank account`}
      subtitle={i18n.str`Choose the credentials you will use to sign in.`}
      footer={
        <span>
          <i18n.Translate>Already have an account?</i18n.Translate>{" "}
          <a
            href={routeLogin.url({})}
            class="font-semibold text-primaryDark hover:text-onPrimaryContainer hover:underline"
          >
            <i18n.Translate>Sign in</i18n.Translate>
          </a>
        </span>
      }
    >
      {operationError ? (
        <AuthAlert type="error">{operationError}</AuthAlert>
      ) : undefined}
      <form
        class="space-y-5"
        noValidate
        onSubmit={(event) => void submit(event)}
        autoCapitalize="none"
        autoCorrect="off"
      >
        <div>
          <label
            htmlFor="register-name"
            class="block text-sm font-medium leading-6 text-onBackground"
          >
            <i18n.Translate>Full name</i18n.Translate>
          </label>
          <div class="mt-2">
            <input
              ref={nameRef}
              type="text"
              name="name"
              id="register-name"
              autocomplete="name"
              enterkeyhint="next"
              class="block w-full rounded-md border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
              value={name}
              required
              aria-invalid={(submitted || !!touched.name) && !!nameError}
              aria-describedby={
                (submitted || touched.name) && nameError
                  ? "register-name-error"
                  : undefined
              }
              onInput={(event) => setName(event.currentTarget.value)}
              onBlur={() => markTouched("name")}
            />
            <FieldError
              id="register-name-error"
              message={nameError}
              visible={submitted || !!touched.name}
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="register-username"
            class="block text-sm font-medium leading-6 text-onBackground"
          >
            <i18n.Translate>Username</i18n.Translate>
          </label>
          <div class="mt-2">
            <input
              ref={usernameRef}
              type="text"
              name="username"
              id="register-username"
              autocomplete="username"
              enterkeyhint="next"
              maxLength={USERNAME_MAX_LENGTH}
              class="block w-full rounded-md border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
              value={username}
              required
              aria-invalid={
                (submitted || !!touched.username) && !!usernameError
              }
              aria-describedby={
                (submitted || touched.username) && usernameError
                  ? "register-username-error"
                  : undefined
              }
              onInput={(event) => setUsername(event.currentTarget.value)}
              onBlur={() => markTouched("username")}
            />
            <FieldError
              id="register-username-error"
              message={usernameError}
              visible={submitted || !!touched.username}
            />
          </div>
        </div>

        <PasswordField
          id="register-password"
          name="password"
          label={i18n.str`Password`}
          value={password}
          error={passwordError}
          showError={submitted || !!touched.password}
          autoComplete="new-password"
          inputRef={passwordRef}
          enterKeyHint="next"
          onInput={setPassword}
          onBlur={() => markTouched("password")}
        />

        <PasswordField
          id="register-confirmation"
          name="password-confirmation"
          label={i18n.str`Confirm password`}
          value={confirmation}
          error={confirmationError}
          showError={submitted || !!touched.confirmation}
          autoComplete="new-password"
          inputRef={confirmationRef}
          enterKeyHint="done"
          onInput={setConfirmation}
          onBlur={() => markTouched("confirmation")}
        />

        <p class="text-sm text-gray-600">
          <i18n.Translate>
            Use between 8 and 64 characters. A longer, unique password is easier
            to keep secure.
          </i18n.Translate>
        </p>

        <button
          type="submit"
          class="flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-wait disabled:bg-gray-300"
          disabled={running}
          aria-busy={running}
        >
          {running ? (
            <i18n.Translate>Creating account…</i18n.Translate>
          ) : (
            <i18n.Translate>Create account</i18n.Translate>
          )}
        </button>
      </form>
    </AuthCard>
  );
}

RegistrationPage.SCREEN_ID = TALER_SCREEN_ID;
export function RegistrationPage({
  routeLogin,
  onAutoLoginSuccess,
  onAutoLoginFailure,
}: {
  routeLogin: RouteDefinition;
  onAutoLoginSuccess(username: string): void;
  onAutoLoginFailure(username: string): void;
}): VNode {
  const { i18n } = useTranslationContext();
  const {
    config,
    lib: { bank: api },
  } = useBankCoreApiContext();
  const [registrationError, setRegistrationError] =
    useState<TranslatedString>();
  const login = useLoginOperation({
    onSuccess: onAutoLoginSuccess,
    onFailure: onAutoLoginFailure,
  });

  const registration = useAsyncAction<
    Awaited<ReturnType<typeof api.createAccount>>,
    [TalerCorebankApi.RegisterAccountRequest]
  >((ct, account) => api.createAccount(undefined, account), {
    onResult: async (result, account) => {
      if (result.type === "ok") {
        setRegistrationError(undefined);
        await login.run(account.username, account.password, []);
        return;
      }
      let message: TranslatedString;
      switch (result.case) {
        case HttpStatusCode.BadRequest:
          message = i18n.str`Check the account details and try again.`;
          break;
        case HttpStatusCode.Unauthorized:
          message = i18n.str`You are not authorized to create this account.`;
          break;
        case TalerErrorCode.BANK_UNALLOWED_DEBIT:
          message = i18n.str`Registration is unavailable because the bank cannot grant the account credit.`;
          break;
        case TalerErrorCode.BANK_RESERVED_USERNAME_CONFLICT:
          message = i18n.str`That username is reserved. Choose another username.`;
          break;
        case TalerErrorCode.BANK_REGISTER_USERNAME_REUSE:
          message = i18n.str`That username is already taken.`;
          break;
        case TalerErrorCode.BANK_REGISTER_PAYTO_URI_REUSE:
          message = i18n.str`That account ID is already taken.`;
          break;
        case TalerErrorCode.BANK_MISSING_TAN_INFO:
          message = i18n.str`The bank is missing information for the selected authentication channel.`;
          break;
        case TalerErrorCode.BANK_TAN_CHANNEL_NOT_SUPPORTED:
          message = i18n.str`The selected authentication channel is not supported.`;
          break;
        case TalerErrorCode.BANK_NON_ADMIN_PATCH_DEBT_LIMIT:
          message = i18n.str`Only an administrator may set the debt limit.`;
          break;
        case TalerErrorCode.BANK_NON_ADMIN_SET_CONVERSION_RATE_CLASS:
          message = i18n.str`Only an administrator may select a conversion rate class.`;
          break;
        case TalerErrorCode.BANK_CONVERSION_RATE_CLASS_UNKNOWN:
          message = i18n.str`The selected conversion rate class does not exist.`;
          break;
        case TalerErrorCode.BANK_NON_ADMIN_SET_TAN_CHANNEL:
          message = i18n.str`Only an administrator may enable two-factor authentication while creating an account.`;
          break;
        case TalerErrorCode.BANK_PASSWORD_TOO_SHORT:
          message = i18n.str`The password must contain at least 8 characters.`;
          break;
        case TalerErrorCode.BANK_PASSWORD_TOO_LONG:
          message = i18n.str`The password must not exceed ${PASSWORD_MAX_LENGTH} characters.`;
          break;
        default:
          assertUnreachable(result);
      }
      setRegistrationError(message);
    },
    onError: () => {
      setRegistrationError(
        i18n.str`Could not reach the bank. Check your connection and try again.`,
      );
    },
  });

  if (!config.allow_registrations) {
    return (
      <AuthCard
        title={i18n.str`Account registration is unavailable`}
        subtitle={i18n.str`This bank is not accepting new account registrations.`}
        footer={
          <a
            href={routeLogin.url({})}
            class="font-semibold text-primaryDark hover:text-onPrimaryContainer hover:underline"
          >
            <i18n.Translate>Return to sign in</i18n.Translate>
          </a>
        }
      >
        <AuthAlert type="warning">
          <i18n.Translate>
            Contact the bank administrator if you need an account.
          </i18n.Translate>
        </AuthAlert>
      </AuthCard>
    );
  }

  return (
    <RegistrationFormView
      routeLogin={routeLogin}
      running={registration.running || login.running}
      operationError={registrationError ?? login.error}
      onSubmit={(values) => {
        setRegistrationError(undefined);
        login.clearError();
        return registration.run(values);
      }}
    />
  );
}
